import {
  ChevronDown,
  Facebook,
  Info,
  Instagram,
  Mail,
  MessageCircle,
  Music2,
  Youtube,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

const legalLinks = [
  { label: "Disclaimer", to: "/disclaimer" },
  { label: "Terms and Conditions", to: "/terms-and-conditions" },
  { label: "Privacy Policy", to: "/privacy-policy" },
];

const companyLinks = [
  // Site-wide entry point into /learn. Without it the topic pages are an orphan island:
  // nothing outside it links in, and Google left all 36 of them uncrawled for months.
  { label: "Topics", to: "/learn" },
  { label: "Guides", to: "/guides" },
  { label: "Compare", to: "/alternatives" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

const discordLink = {
  href: "https://discord.gg/tEwkkM4Q43",
  labelKey: "landing.footerDiscordCta",
  icon: MessageCircle,
};

const socialHandleLinks = [
  {
    href: "https://www.tiktok.com/@flexlearnai8",
    handleKey: "landing.footerTikTokHandle",
    icon: Music2,
  },
  {
    href: "https://www.instagram.com/flexlearnai8/",
    handleKey: "landing.footerInstagramHandle",
    icon: Instagram,
  },
  {
    href: "https://www.youtube.com/@FlexLearnAI",
    handleKey: "landing.footerYouTubeHandle",
    icon: Youtube,
  },
  {
    href: "https://www.facebook.com/people/FlexLearnai/61589708818075/",
    handleKey: "landing.footerFacebookHandle",
    icon: Facebook,
  },
];

const importantDisclaimer =
  "Important Disclaimer: FlexLearn AI is an AI-powered educational tool. It is designed to help users study, review materials, and generate practice content. FlexLearn AI does not guarantee the accuracy, completeness, reliability, or educational effectiveness of any AI-generated content. Users are responsible for reviewing all outputs and verifying information against original materials, instructors, textbooks, official test resources, or other qualified sources. FlexLearn AI does not guarantee grades, test scores, academic performance, admission results, certifications, or any specific outcome.";

export default function SiteFooter() {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();
  const DiscordIcon = discordLink.icon;

  return (
    <footer className="border-t border-border px-6 py-8">
      <div className="mx-auto grid max-w-6xl gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(240px,360px)_minmax(0,1fr)] lg:items-start">
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <span className="font-heading text-xl font-black leading-none">
              <span className="text-secondary">Flex</span>
              <span className="text-terracotta">LearnAI</span>
            </span>
            <img
              src="/logo-small.v1.webp"
              alt="FlexLearnAI"
              className="h-9 w-auto"
              width="36"
              height="36"
              loading="lazy"
              decoding="async"
              onError={(event) => {
                event.currentTarget.onerror = null;
                event.currentTarget.src = "/logo-small.png";
              }}
            />
          </div>

          <nav aria-label="Company">
            <ul className="flex max-w-2xl flex-wrap items-center gap-x-3 gap-y-2 text-sm text-muted-foreground">
              {companyLinks.map((link, index) => (
                <li key={link.label} className="inline-flex items-center gap-3">
                  {index > 0 && (
                    <span aria-hidden="true" className="text-muted-foreground/60">
                      |
                    </span>
                  )}
                  <Link
                    to={link.to}
                    className="underline underline-offset-2 transition-colors hover:text-foreground"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <nav aria-label="Legal">
            <ul className="flex max-w-2xl flex-wrap items-center gap-x-3 gap-y-2 text-sm text-muted-foreground">
              {legalLinks.map((link, index) => (
                <li key={link.label} className="inline-flex items-center gap-3">
                  {index > 0 && (
                    <span aria-hidden="true" className="text-muted-foreground/60">
                      |
                    </span>
                  )}
                  {link.to ? (
                    <Link
                      to={link.to}
                      className="underline underline-offset-2 transition-colors hover:text-foreground"
                    >
                      {link.label}
                    </Link>
                  ) : (
                    <a
                      href={link.href}
                      className="underline underline-offset-2 transition-colors hover:text-foreground"
                    >
                      {link.label}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </nav>

          <p className="text-sm text-muted-foreground">
            {t("landing.footerCopyright", { year: currentYear })}
          </p>
        </div>

        <details className="group rounded-2xl border border-border/70 bg-surface/70 px-4 py-3 text-xs leading-relaxed text-muted-foreground lg:mx-auto lg:w-full">
          <summary className="flex cursor-pointer list-none items-center justify-between gap-3 text-foreground marker:hidden [&::-webkit-details-marker]:hidden">
            <span className="inline-flex items-center gap-2 font-semibold">
              <Info className="h-4 w-4 text-terracotta" aria-hidden="true" />
              Important Disclaimer
            </span>
            <ChevronDown
              className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180"
              aria-hidden="true"
            />
          </summary>
          <p className="mt-2 text-muted-foreground">{importantDisclaimer}</p>
        </details>

        <div className="space-y-3 text-sm text-muted-foreground sm:text-right">
          <a
            href="mailto:info@flexlearnai.com"
            className="flex w-fit items-center gap-1.5 underline underline-offset-2 transition-colors hover:text-foreground sm:ml-auto"
          >
            <Mail className="h-4 w-4" aria-hidden="true" />
            Contact
          </a>

          <a
            href={discordLink.href}
            target="_blank"
            rel="noreferrer"
            className="flex w-fit items-center gap-1.5 underline underline-offset-2 transition-colors hover:text-foreground sm:ml-auto"
          >
            <DiscordIcon className="h-4 w-4" aria-hidden="true" />
            {t(discordLink.labelKey)}
          </a>

          <div className="space-y-1">
            <p>{t("landing.footerFollowUsOn")}</p>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2 sm:justify-end">
              {socialHandleLinks.map(({ href, handleKey, icon: Icon }) => (
                <a
                  key={href}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 underline underline-offset-2 transition-colors hover:text-foreground"
                >
                  <Icon className="h-4 w-4" aria-hidden="true" />
                  {t(handleKey)}
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
